import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import AccessGate from './components/AccessGate';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotMePage from './pages/NotMePage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import RegisterPage from './pages/RegisterPage';
import WelcomePage from './pages/WelcomePage';
import SsoContinuePage from './pages/SsoContinuePage';
import DevelopersPage from './pages/DevelopersPage';
import { owesEssentials } from './lib/essentialsGate';
import {
  TermsPage, PrivacyPage, ContactPage, SubProcessorsPage, CookiesPage, AcceptableUsePage, TaxStatementPage,
} from './pages/LegalPage';
import DashboardPage from './pages/DashboardPage';
import TeamCalendarPage from './pages/TeamCalendarPage';
import NewLeavePage from './pages/NewLeavePage';
import MyLeavePage from './pages/MyLeavePage';
import SupportPage from './pages/SupportPage';
import UserLeavePage from './pages/UserLeavePage';
import EmployeesPage from './pages/EmployeesPage';
import LocationsPage from './pages/LocationsPage';
import OrgChartPage from './pages/OrgChartPage';
// Reports (DESIGN-reports.md step 0b): a home page and one page per report.
import ReportsHomePage from './pages/reports/ReportsHomePage';
import AbsenceOverTimePage from './pages/reports/AbsenceOverTimePage';
import WhatWeOwePage from './pages/reports/WhatWeOwePage';
import ManagersKeepingUpPage from './pages/reports/ManagersKeepingUpPage';
import SicknessPage from './pages/reports/SicknessPage';
import GettingABreakPage from './pages/reports/GettingABreakPage';
import PayrollPage from './pages/PayrollPage';
import SettingsPage from './pages/SettingsPage';
import HolidaysPage from './pages/HolidaysPage';
import ProfilePage from './pages/ProfilePage';
import SuperadminPage from './pages/SuperadminPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import OnboardingPage from './pages/OnboardingPage';
import ActivityLogPage from './pages/ActivityLogPage';
import BillingPage from './pages/BillingPage';
import ApprovalPoliciesPage from './pages/ApprovalPoliciesPage';
import DevToolbar from './components/DevToolbar';
import AccountClosingBanner from './components/AccountClosingBanner';
import ImpersonationBanner from './components/ImpersonationBanner';
import SlackOfferBar from './components/SlackOfferBar';
import BillingStatus from './components/BillingStatus';
import { getHostInfo, isPrelaunchHost } from './lib/hosts';
import PrelaunchPage from './pages/PrelaunchPage';
import BrandedToaster from './components/BrandedToaster';
import './App.css';

// On the marketing (apex) surface, any non-marketing path is really an app URL,
// so send the visitor over to the app subdomain, preserving path + query + hash.
const CrossHostRedirect = ({ origin }) => {
  React.useEffect(() => {
    const { pathname, search, hash } = window.location;
    window.location.replace(`${origin}${pathname}${search}${hash}`);
  }, [origin]);
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

// Google OAuth Client ID (set in environment or leave empty to disable)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

/**
 * Does this account still owe us the three answers sign-up stopped asking for?
 *
 * ⚠️ THIS IS THE GATE, AND IT HAS TO BE A REDIRECT, NOT A BANNER. Onboarding already had a
 * "complete setup" banner on the dashboard, which anyone could ignore for ever. That was
 * fine while sign-up collected the name up front. It is not fine now: sign-up creates the
 * account WITHOUT a name or a company name, so ignoring the prompt means wandering the app
 * as a nameless person on the wallchart, the org chart and every approval email.
 *
 * Read off the session rather than asking the server on every render — /welcome asks the
 * server itself and sends anyone who has already answered straight on, so the cost of
 * being wrong here is one redirect, not a wrong answer.
 */
// The rule itself lives in lib/essentialsGate.js so it can be tested for real rather than
// through a hand-copied duplicate — see the note in that file.

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user, tenant } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (owesEssentials(user, tenant)) {
    return <Navigate to="/welcome" replace />;
  }

  return children;
};

// Admin-only route wrapper (admin or superadmin)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Admin and superadmin can access
  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Reports route — admins, and managers given "Can view reports" (DESIGN-reports.md C).
// ⚠️ This only decides whether the PAGE opens. Every report route on the server checks
// the permission itself (permissions.require_reports) — a hidden page protects nothing.
const ReportsRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isAdminRole = user?.role === 'admin' || user?.role === 'superadmin';
  if (!isAdminRole && !user?.can_view_reports) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Settings route - only for admin and superadmin
const SettingsRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Only admin and superadmin can access settings
  if (user?.role !== 'admin' && user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Manager or Admin route wrapper
const ManagerRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  // Manager, admin, and superadmin can access
  if (user?.role !== 'manager' && user?.role !== 'admin' && user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public route - redirect to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // Allow navigation to onboarding even if authenticated
  if (isAuthenticated && location.pathname !== '/onboarding') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const { isApp, appOrigin } = getHostInfo();

  // MARKETING surface (apex): the landing page and the public legal pages live
  // here. Everything else is an app route, so bounce it to the app subdomain.
  // /terms and /privacy are served on BOTH hosts: the landing page footer links
  // to them here, the sign-up screen links to them on app.*, and Google's and
  // Microsoft's reviewers open whichever URL is on the consent screen.
  if (!isApp) {
    return (
      <Routes>
        <Route path="/" element={isPrelaunchHost() ? <PrelaunchPage /> : <LandingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/sub-processors" element={<SubProcessorsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePage />} />
        <Route path="/tax" element={<TaxStatementPage />} />
        {/* The public API's page (DESIGN-api-and-webhooks.md) — both hosts, like the legal pages. */}
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="*" element={<CrossHostRedirect origin={appOrigin} />} />
      </Routes>
    );
  }

  // APP surface (app.* or bare localhost): the product. Root goes to the
  // dashboard, which itself bounces to /login when signed out — the marketing
  // landing page is never shown here.
  return (
    <>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      } />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      {/* ⚠️ NOT wrapped in PublicRoute or ProtectedRoute, and that is deliberate. Whoever
          is here is in between: signed up, not confirmed, holding a token scoped to this
          screen alone. PublicRoute would bounce them out once they were logged in via the
          safety-net route; ProtectedRoute would refuse them while they are mid-sign-up.
          The gate does its own checking — it asks the server what is still owed. */}
      <Route path="/welcome" element={<WelcomePage />} />
      {/* Where Google and Microsoft drop people back. ⚠️ Not wrapped in PublicRoute:
          whoever lands here may or may not already have a session, and bouncing them
          would strand the one-time code unspent. */}
      <Route path="/sso/continue" element={<SsoContinuePage />} />
      {/* "It wasn't me", from the "Was this you?" email (DESIGN-security.md step 2). ⚠️ Not
          wrapped in PublicRoute: somebody signed in in this browser must still reach the
          button, not be bounced to the dashboard. Opening it changes nothing by itself. */}
      <Route path="/not-me" element={<NotMePage />} />
      {/* Holding pages. ⚠️ The documents behind these do not exist yet — PAGES-NEEDED.md */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/sub-processors" element={<SubProcessorsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/acceptable-use" element={<AcceptableUsePage />} />
      <Route path="/tax" element={<TaxStatementPage />} />
      <Route path="/developers" element={<DevelopersPage />} />


      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      {/* The wallchart's own page (Warm Hive decision 9, 29/10/2026) — every role. */}
      <Route path="/team-calendar" element={
        <ProtectedRoute>
          <TeamCalendarPage />
        </ProtectedRoute>
      } />
      <Route path="/leave/new" element={
        <ProtectedRoute>
          <NewLeavePage />
        </ProtectedRoute>
      } />
      <Route path="/leave/user/:userId" element={
        <ManagerRoute>
          <UserLeavePage />
        </ManagerRoute>
      } />
      <Route path="/leave" element={
        <ProtectedRoute>
          <MyLeavePage />
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute>
          <SupportPage />
        </ProtectedRoute>
      } />
      <Route path="/approvals" element={
        <ManagerRoute>
          <DashboardPage />
        </ManagerRoute>
      } />
      <Route path="/employees" element={
        <ManagerRoute>
          <EmployeesPage />
        </ManagerRoute>
      } />
      <Route path="/locations" element={
        <AdminRoute>
          <LocationsPage />
        </AdminRoute>
      } />
      <Route path="/org-chart" element={
        <AdminRoute>
          <OrgChartPage />
        </AdminRoute>
      } />
      <Route path="/reports" element={
        <ReportsRoute>
          <ReportsHomePage />
        </ReportsRoute>
      } />
      <Route path="/reports/absence-over-time" element={
        <ReportsRoute>
          <AbsenceOverTimePage />
        </ReportsRoute>
      } />
      <Route path="/reports/what-we-owe" element={
        <ReportsRoute>
          <WhatWeOwePage />
        </ReportsRoute>
      } />
      <Route path="/reports/managers-keeping-up" element={
        <ReportsRoute>
          <ManagersKeepingUpPage />
        </ReportsRoute>
      } />
      <Route path="/reports/sickness" element={
        <ReportsRoute>
          <SicknessPage />
        </ReportsRoute>
      } />
      <Route path="/reports/getting-a-break" element={
        <ReportsRoute>
          <GettingABreakPage />
        </ReportsRoute>
      } />
      {/*
        ⚠️ ProtectedRoute, NOT AdminRoute. Payroll follows the PERMISSION, not the
        role — Sandra in accounts is an ordinary employee who runs payroll, and an
        AdminRoute here would lock out the person the feature was built for.
        The page itself shows a plain "you don't have permission" panel when the
        API says no, and the API is what actually decides.
      */}
      <Route path="/payroll" element={
        <ProtectedRoute>
          <PayrollPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <SettingsRoute>
          <SettingsPage />
        </SettingsRoute>
      } />
      <Route path="/holidays" element={
        <AdminRoute>
          <HolidaysPage />
        </AdminRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/superadmin" element={
        <ProtectedRoute>
          <SuperadminPage />
        </ProtectedRoute>
      } />
      <Route path="/superadmin/customers/:customerId" element={
        <ProtectedRoute>
          <CustomerDetailPage />
        </ProtectedRoute>
      } />
      <Route path="/superadmin/activity" element={
        <ProtectedRoute>
          <ActivityLogPage />
        </ProtectedRoute>
      } />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/billing" element={
        <Navigate to="/settings?tab=billing" replace />
      } />
      <Route path="/settings/approval-policies" element={
        <AdminRoute>
          <ApprovalPoliciesPage />
        </AdminRoute>
      } />
      
      {/* OAuth callback route */}
      <Route path="/auth/callback" element={<OAuthCallback />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {/* App-wide billing gate (Phase 4) — trial countdown / dunning banner for
        admins, and the paywall when a tenant is blocked. Self-gates to nothing
        for active/paid tenants and superadmins. */}
    <BillingStatus />
    {/* App-wide off-boarding countdown — self-gates to admins of a closing
        tenant, renders nothing otherwise (incl. all public pages). */}
    <AccountClosingBanner />
    {/* App-wide "you're acting as a user" bar during a consent-login support session. */}
    <ImpersonationBanner />
    {/* The one-off "Your company uses Slack" offer (DESIGN-slack.md H3a, Rob's L1) — the
        server decides; renders nothing for nearly everybody nearly always. */}
    <SlackOfferBar />
    </>
  );
}

// OAuth callback handler
const OAuthCallback = () => {
  const navigate = useNavigate();
  
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (token) {
      localStorage.setItem('token', token);
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
};

function App() {
  const googleClientId = GOOGLE_CLIENT_ID;
  
  const AppContent = (
    <AccessGate>
      <BrowserRouter>
        <ThemeProvider>
          <AuthProvider>
            <DevToolbar />
            <BrandedToaster />
            <AppRoutes />
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AccessGate>
  );
  
  // Wrap with GoogleOAuthProvider only if client ID is set
  if (googleClientId) {
    return (
      <GoogleOAuthProvider clientId={googleClientId}>
        {AppContent}
      </GoogleOAuthProvider>
    );
  }
  
  return AppContent;
}

export default App;
